import { assetPaths } from "./markdownImages.js";

const DATABASE_NAME = "md2pdf";
const STORE_NAME = "image-assets";
const DATABASE_VERSION = 1;

const canUseIndexedDB = () =>
  typeof window !== "undefined" && Boolean(window.indexedDB);

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!canUseIndexedDB()) {
      resolve(null);
      return;
    }
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME, { keyPath: "path" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const finishTransaction = transaction =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

export const persistImageFiles = async (
  files,
  markdownFile,
  { replace = false } = {}
) => {
  const database = await openDatabase().catch(() => null);
  if (!database) return;

  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    if (replace) store.clear();
    for (const file of files || []) {
      for (const path of assetPaths(file, markdownFile)) {
        store.put({ path, blob: file });
      }
    }
    await finishTransaction(transaction);
  } finally {
    database.close();
  }
};

export const restoreImageAssets = async () => {
  const database = await openDatabase().catch(() => null);
  if (!database) return {};

  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).getAll();
    const records = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    const assets = {};
    for (const record of records) {
      assets[record.path] = URL.createObjectURL(record.blob);
    }
    return assets;
  } catch (error) {
    return {};
  } finally {
    database.close();
  }
};

export const clearPersistedImageFiles = async () => {
  const database = await openDatabase().catch(() => null);
  if (!database) return;

  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).clear();
    await finishTransaction(transaction);
  } finally {
    database.close();
  }
};
